/* eslint-disable react/prop-types */
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import Chart from 'react-google-charts';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Input, Form, FormGroup, Label, Col, Button } from 'reactstrap';

import {
  loadAssetAllocationScenarios,
  loadScenariosInputs,
  saveScenariosInputs,
} from '../../../actions/Resources';
import useGoogleChartTooltip from '../../../hooks/useGoogleChartTooltip';

const DEFAULT_SCENARIOS_INPUTS = {
  cashInYear1: 0,
  cashInYear2: 0,
  cashInYear3: 0,
  cashInYear4: 0,
  cashInYear5: 0,
  cashOutYear1: 0,
  cashOutYear2: 0,
  cashOutYear3: 0,
  cashOutYear4: 0,
  cashOutYear5: 0,
};

const ScenariosTab = (props) => {
  const [scenariosInputs, setScenariosInputs] = useState(DEFAULT_SCENARIOS_INPUTS);
  const [calculationType, setCalculationType] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [selectedScenarios, setSelectedScenarios] = useState([
    'Base Case',
    'Depression',
  ]);

  const { setChartContainerRef, followTooltip } = useGoogleChartTooltip(
    'top',
    'currency'
  );

  const chartResizeObserverRef = useRef(null);
  const dispatchWindowResize = useMemo(
    () => _.throttle(() => window.dispatchEvent(new Event('resize')), 200),
    []
  );

  const setChartResizeRef = useCallback(
    (node) => {
      setChartContainerRef(node);
      if (chartResizeObserverRef.current) {
        chartResizeObserverRef.current.disconnect();
        chartResizeObserverRef.current = null;
      }
      if (node && typeof ResizeObserver !== 'undefined') {
        chartResizeObserverRef.current = new ResizeObserver(dispatchWindowResize);
        chartResizeObserverRef.current.observe(node);
      }
    },
    [setChartContainerRef, dispatchWindowResize]
  );

  useEffect(
    () => () => {
      if (chartResizeObserverRef.current) {
        chartResizeObserverRef.current.disconnect();
      }
      dispatchWindowResize.cancel();
    },
    [dispatchWindowResize]
  );

  const handleSelectedScenarios = (e) => {
    const _scenarios = new Set();

    if (selectedScenarios.includes(e) && selectedScenarios.length > 1) {
      const newScenarioSelection = selectedScenarios.filter(
        (scenario) => scenario !== e
      );
      return setSelectedScenarios(newScenarioSelection);
    }
    selectedScenarios.forEach((scenario) => {
      _scenarios.add(scenario);
    });
    _scenarios.add(e);

    setSelectedScenarios([..._scenarios]);
  };

  const getData = useMemo(() => {
    const resp = [];

    const graphHeader = [
      ['Scenario', 'Year 0', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
    ];

    selectedScenarios.forEach((ss) => {
      const node = props.assetAllocationScenarios.find((node) =>
        node.scenarioName.startsWith(ss)
      );

      const calculationLiquid = (lastYearCalc, year) =>
        lastYearCalc * (1 + node[`year${year}`]) +
        (scenariosInputs[`cashInYear${year}`] - scenariosInputs[`cashOutYear${year}`]);

      const calculationPercentual = (lastYearCalc, year) =>
        lastYearCalc * (1 + node[`year${year}`]) +
        (((1 + scenariosInputs[`cashInYear${year}`]) / 100) * lastYearCalc -
          ((1 + scenariosInputs[`cashOutYear${year}`]) / 100) * lastYearCalc);

      if (node) {
        const effectiveYear0 = Number(
          (Number(node.marketValueYear0) || 0).toFixed(2)
        );
        const year1Calculation = calculationType
          ? calculationPercentual(effectiveYear0, '1')
          : calculationLiquid(effectiveYear0, '1');
        const year2Calculation = calculationType
          ? calculationPercentual(year1Calculation, '2')
          : calculationLiquid(year1Calculation, '2');
        const year3Calculation = calculationType
          ? calculationPercentual(year2Calculation, '3')
          : calculationLiquid(year2Calculation, '3');
        const year4Calculation = calculationType
          ? calculationPercentual(year3Calculation, '4')
          : calculationLiquid(year3Calculation, '4');
        const year5Calculation = calculationType
          ? calculationPercentual(year4Calculation, '5')
          : calculationLiquid(year4Calculation, '5');
        resp.push([
          node.scenarioName,
          effectiveYear0,
          Number(year1Calculation.toFixed(2)),
          Number(year2Calculation.toFixed(2)),
          Number(year3Calculation.toFixed(2)),
          Number(year4Calculation.toFixed(2)),
          Number(year5Calculation.toFixed(2)),
        ]);
      }
    });

    const graphData = [...graphHeader, ...resp];
    return graphData[0].map((_, colIndex) =>
      graphData.map((row) => row[colIndex])
    );
  }, [
    scenariosInputs,
    props.assetAllocationScenarios,
    selectedScenarios,
    calculationType,
  ]);

  const marketValueYear0 = useMemo(() => {
    const primaryNode = props.assetAllocationScenarios?.find((node) =>
      selectedScenarios.some((ss) => node.scenarioName.startsWith(ss))
    );
    return primaryNode
      ? Math.round(Number(primaryNode.marketValueYear0) || 0)
      : 0;
  }, [props.assetAllocationScenarios, selectedScenarios]);

  const parseNodes = (_selectedCalcValue) =>
    props.assetAllocationScenarios.map((item) => item.scenarioName);

  const scenarioOptions = parseNodes();

  const RenderYearInputLine = useCallback(
    (year) => {
      const cashInField = `cashInYear${year}`;
      const cashOutField = `cashOutYear${year}`;
      const handleChangeCashIn = (event) => {
        setScenariosInputs({ ...scenariosInputs, [cashInField]: Number(event.target.value) });
        setHasUnsavedChanges(true);
      };
      const handleChangeCashOut = (event) => {
        setScenariosInputs({
          ...scenariosInputs,
          [cashOutField]: Number(event.target.value) > 0 ? Number(event.target.value) : 0,
        });
        setHasUnsavedChanges(true);
      };

      return (
        <FormGroup>
          <Label className="year-input" xs={2} sm={2} md={3} lg={12}>
            {year > 0 ? `Year ${year}` : ''}
          </Label>
          <Col xs={2} sm={3} md={12} lg={12}>
            <Input
              className={year === 0 ? 'year-zero-input' : undefined}
              onChange={handleChangeCashIn}
              value={
                year === 0
                  ? `$${marketValueYear0.toLocaleString('en-US')}`
                  : scenariosInputs[cashInField]
              }
              id={`${cashInField}-input`}
              name={cashInField}
              type={year === 0 ? 'text' : 'number'}
              disabled={year === 0}
            />
          </Col>
          <Col xs={2} sm={3} md={12} lg={12}>
            {year !== 0 && (
              <Input
                onChange={handleChangeCashOut}
                value={scenariosInputs[cashOutField]}
                id={`${cashOutField}-input`}
                name={cashOutField}
                type="number"
              />
            )}
          </Col>
        </FormGroup>
      );
    },
    [scenariosInputs, marketValueYear0]
  );

  const buttons = _.map(scenarioOptions, (scenario) => {
    const isSelected = selectedScenarios?.includes(scenario);
    const color = isSelected ? '' : 'dark';
    return (
      <Button
        key={scenario}
        className={
          isSelected
            ? 'shadow-none selected filterButton'
            : 'shadow-none filterButton'
        }
        outline={!isSelected}
        color={color}
        onClick={() => handleSelectedScenarios(scenario)}>
        {scenario}
      </Button>
    );
  });

  const chartOptions = useMemo(
    () => ({
      height: 400,
      width: '100%',
      chartArea: { top: 30, right: -10, bottom: 80, left: 90 },
      animation: { duration: 100, easing: 'out' },
      legend: { position: 'bottom' },
      backgroundColor: { fill: 'transparent', opacity: 100 },
      tooltip: { isHtml: true },
      colors: [
        '#002060',
        '#16709E',
        '#6ED0F7',
        '#8CC94A',
        '#D6F28C',
        '#F2CC73',
        '#43505E',
        '#8596A8',
      ],
      lineWidth: 4,
      pointSize: 6,
      pointShape: 'circle',
      series: {
        0: { lineDashStyle: [14, 2, 7, 2] },
        1: { lineDashStyle: [14, 2, 2, 7] },
        2: { lineDashStyle: [2, 2] },
        3: { lineDashStyle: [4, 4] },
        4: { lineDashStyle: [5, 1, 3] },
        5: { lineDashStyle: [4, 1] },
        6: { lineDashStyle: [10, 2] },
        7: { lineDashStyle: [1, 1] },
        8: { lineDashStyle: [2, 2, 20, 2, 20, 2] },
      },
      hAxis: {
        textPosition: 'none',
        baselineColor: 'none',
        ticks: [],
      },
    }),
    []
  );

  useEffect(() => {
    if (props.planId) {
      props.loadScenariosInputs(props.planId);
    }
  }, [props.planId]);

  useEffect(() => {
    const data = props.scenariosInputs?.data;
    if (!data) return;
    setScenariosInputs({
      cashInYear1: data.cashInYear1 ?? 0,
      cashInYear2: data.cashInYear2 ?? 0,
      cashInYear3: data.cashInYear3 ?? 0,
      cashInYear4: data.cashInYear4 ?? 0,
      cashInYear5: data.cashInYear5 ?? 0,
      cashOutYear1: data.cashOutYear1 ?? 0,
      cashOutYear2: data.cashOutYear2 ?? 0,
      cashOutYear3: data.cashOutYear3 ?? 0,
      cashOutYear4: data.cashOutYear4 ?? 0,
      cashOutYear5: data.cashOutYear5 ?? 0,
    });
    setCalculationType(data.isPercentageValues ?? false);
    setHasUnsavedChanges(false);
  }, [props.scenariosInputs?.data]);

  const handleSave = () => {
    props.saveScenariosInputs(props.planId, {
      planId: props.planId,
      isPercentageValues: calculationType,
      ...scenariosInputs,
    });
    setHasUnsavedChanges(false);
  };

  const changeCalculationType = () => {
    setCalculationType((prev) => !prev);
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    if (props.myManagersList.data) {
      const managerValueSum = _.sumBy(
        props.myManagersList.data,
        (manager) => manager.assetValue
      );

      const mixWeights = props.myManagersList.data.map((manager) => {
        return {
          component_display_name: manager.strategyName,
          component_aamb_pk: Number(manager.aambPk),
          clientHub: manager.assetValue / managerValueSum,
        };
      });

      const dataToSend = {
        mixweights: mixWeights,
        version_id: ['15'],
        mixdollars: [
          {
            mix_guid: 'clientHub',
            dollar_value: managerValueSum,
          },
        ],
      };

      props.loadAssetAllocationScenarios(dataToSend);
    }
  }, [props.myManagersList.loading]);

  const renderChart = () => {
    if (
      !props.assetAllocationScenarios ||
      !getData ||
      getData[0]?.length === 1
    ) {
      return (
        <div className="loading-scenarios">
          <div className="spinnerIcon" />
        </div>
      );
    } else {
      return (
        <Col sm={12} md={8} lg={9} className="scenarios-content">
          <div className="scenarios-body">
            <Form style={{ alignItems: "center" }}>
              <FormGroup style={{ textAlign: 'center', justifyContent: 'space-evenly' }}>
                <Col xs={2} sm={2} md={6} lg={12}>
                  <div className="switchGroup">
                    <div id="dollar-calculation">$</div>
                    <FormGroup switch disabled>
                      <Input
                        type="switch"
                        name="calculation-type"
                        id="calculation-type-switch"
                        checked={calculationType}
                        onChange={(e) => changeCalculationType(e)}
                      />
                      <Label check>%</Label>
                    </FormGroup>
                  </div>
                </Col>
                <Label xs={2} sm={3} md={9} lg={12}>
                  Cash In
                </Label>
                <Label xs={2} sm={3} md={9} lg={12}>
                  Cash Out
                </Label>
              </FormGroup>
              {RenderYearInputLine(0)}
              {RenderYearInputLine(1)}
              {RenderYearInputLine(2)}
              {RenderYearInputLine(3)}
              {RenderYearInputLine(4)}
              {RenderYearInputLine(5)}
            </Form>
            {hasUnsavedChanges ? (
              <div className="scenarios-save-prompt">
                <Button color="primary" onClick={handleSave}>
                  <i className="fa-solid fa-arrows-rotate me-2" />
                  Recalculate
                </Button>
              </div>
            ) : (
              <div
                className="google-chart-tooltip-container scenarios-chart-container"
                ref={setChartResizeRef}
                onMouseMove={followTooltip}
              >
                <Chart
                  chartType="LineChart"
                  loader={<div className="spinnerIcon" />}
                  data={getData}
                  options={chartOptions}
                />
              </div>
            )}
          </div>

          <div className='right-buttons'>
            <div className="multiselect">
              {buttons}
            </div>
          </div>
        </Col>
      );
    }
  };

  return <div className="scenarios">{renderChart()}</div>;
};

ScenariosTab.propTypes = {
  assetAllocationScenarios: PropTypes.array,
  loadAssetAllocationScenarios: PropTypes.func.isRequired,
  loadScenariosInputs: PropTypes.func.isRequired,
  saveScenariosInputs: PropTypes.func.isRequired,
  myManagersList: PropTypes.object,
  planId: PropTypes.string,
  scenariosInputs: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    assetAllocationScenarios:
      state.resources.asset_allocation_scenarios.data || [],
    myManagersList: state.resources.my_managers || {},
    planId: state.ui.selectedPlan?.planId,
    scenariosInputs: state.resources.scenarios_inputs,
  };
};

export default connect(mapStateToProps, {
  loadAssetAllocationScenarios,
  loadScenariosInputs,
  saveScenariosInputs,
})(ScenariosTab);
